import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { CachedTopic } from "@/types/intent";

export interface TopicsState {
  topicIds: string[];
  topicsCache: CachedTopic[];
  collectionId: string | null;
  collectionName: string | null;
  topicsInclusionMethod: "ANY_PRESENT" | "ALL_PRESENT";
}

export interface TopicsStateApi extends TopicsState {
  update: (patch: Partial<TopicsState>) => void;
}

const TopicsStateContext = createContext<TopicsStateApi | null>(null);

export function useTopicsStateContext(): TopicsStateApi | null {
  return useContext(TopicsStateContext);
}

export const LocalTopicsProvider: FC<{
  children: ReactNode;
  initial?: Partial<TopicsState>;
  onStateChange?: (state: TopicsState) => void;
}> = ({ children, initial, onStateChange }) => {
  const [state, setState] = useState<TopicsState>({
    topicIds: initial?.topicIds ?? [],
    topicsCache: initial?.topicsCache ?? [],
    collectionId: initial?.collectionId ?? null,
    collectionName: initial?.collectionName ?? null,
    topicsInclusionMethod: initial?.topicsInclusionMethod ?? "ANY_PRESENT",
  });

  const update = useCallback(
    (patch: Partial<TopicsState>) => {
      setState((prev) => {
        const next = { ...prev, ...patch };
        onStateChange?.(next);
        return next;
      });
    },
    [onStateChange]
  );

  const value = useMemo(() => ({ ...state, update }), [state, update]);

  return (
    <TopicsStateContext.Provider value={value}>
      {children}
    </TopicsStateContext.Provider>
  );
};
